// API hook for business directory with infinite scroll support
import { useState, useEffect, useCallback, useRef } from "react";
import {
  BusinessListing,
  BusinessDirectoryResponse,
  BusinessDirectoryParams,
  BusinessDirectoryMeta,
  DirectoryFilters,
  DirectoryFiltersResponse,
} from "@/types/directory";

const REST_V2_API_BASE =
  import.meta.env.VITE_API_BASE_URL || "https://api-staging.withsplice.com";

interface UseBusinessDirectoryResult {
  businesses: BusinessListing[];
  loading: boolean;
  loadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  meta: BusinessDirectoryMeta | null;
  loadMore: () => void;
  refresh: () => void;
}

export const useBusinessDirectory = (
  params: BusinessDirectoryParams = {}
): UseBusinessDirectoryResult => {
  const [businesses, setBusinesses] = useState<BusinessListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [meta, setMeta] = useState<BusinessDirectoryMeta | null>(null);
  const [page, setPage] = useState(1);

  // Track the current search/filter params to reset on change
  const paramsRef = useRef(params);

  const fetchBusinesses = useCallback(
    async (pageNum: number, isLoadMore: boolean = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", pageNum.toString());
        queryParams.append("per_page", (params.per_page || 12).toString());

        if (params.search) {
          queryParams.append("search", params.search);
        }

        if (params.specialty) {
          queryParams.append("specialty", params.specialty);
        }

        if (params.city) {
          queryParams.append("city", params.city);
        }

        if (params.state) {
          queryParams.append("state", params.state);
        }

        if (params.min_rating) {
          queryParams.append("min_rating", params.min_rating.toString());
        }

        const response = await fetch(
          `${REST_V2_API_BASE}/v2/businesses?${queryParams.toString()}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch businesses: ${response.status}`);
        }

        const data: BusinessDirectoryResponse = await response.json();

        if (isLoadMore) {
          setBusinesses((prev) => [...prev, ...data.businesses]);
        } else {
          setBusinesses(data.businesses);
        }

        setMeta(data.meta);
        setError(null);
      } catch (err) {
        console.error("[BusinessDirectory] Error fetching:", err);
        setError(err as Error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [params.search, params.specialty, params.city, params.state, params.min_rating, params.per_page]
  );

  // Reset and fetch when params change
  useEffect(() => {
    const paramsChanged =
      paramsRef.current.search !== params.search ||
      paramsRef.current.specialty !== params.specialty ||
      paramsRef.current.city !== params.city ||
      paramsRef.current.state !== params.state ||
      paramsRef.current.min_rating !== params.min_rating;

    if (paramsChanged) {
      paramsRef.current = params;
      setPage(1);
      setBusinesses([]);
      fetchBusinesses(1, false);
    }
  }, [params.search, params.specialty, params.city, params.state, params.min_rating, fetchBusinesses]);

  // Initial fetch
  useEffect(() => {
    fetchBusinesses(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(() => {
    if (meta?.has_more && !loadingMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchBusinesses(nextPage, true);
    }
  }, [meta?.has_more, loadingMore, loading, page, fetchBusinesses]);

  const refresh = useCallback(() => {
    setPage(1);
    setBusinesses([]);
    fetchBusinesses(1, false);
  }, [fetchBusinesses]);

  return {
    businesses,
    loading,
    loadingMore,
    error,
    hasMore: meta?.has_more ?? false,
    meta,
    loadMore,
    refresh,
  };
};

// Hook to fetch available filter options
export const useDirectoryFilters = () => {
  const [filters, setFilters] = useState<DirectoryFilters | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await fetch(`${REST_V2_API_BASE}/v2/businesses/filters`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch filters: ${response.status}`);
        }

        const data: DirectoryFiltersResponse = await response.json();
        setFilters(data.filters);
        setError(null);
      } catch (err) {
        console.error("[DirectoryFilters] Error fetching:", err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilters();
  }, []);

  return { filters, loading, error };
};
